/**
 * Widget V2 — Legal pages (/legal/:page)
 *
 * Terms & Conditions and the Privacy & Cookie Policy, ported from the legacy
 * widget. Slug-less, so it renders outside the widget shell (the checkout's
 * terms checkbox opens it in a new tab) — it carries its own header and back
 * link rather than the app's AppBar.
 */

import React, { useMemo } from "react";
import { useParams, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { ThemeProvider, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { createWidgetTheme } from "/imports/ui/styles/theme";
import TermsContent from "./TermsContent";
import PrivacyContent from "./PrivacyContent";

const PAGES = [
  { path: "terms", label: "Terms & Conditions" },
  { path: "privacy", label: "Privacy Policy" }
];

// The copy is plain h1/h2/h3/p/ul so the ported legal text stays verbatim; the
// widget's type scale is applied here rather than by rewriting every tag.
const COPY_SX = {
  "& h1": { fontSize: { xs: "1.4rem", md: "1.6rem" }, fontWeight: 800, letterSpacing: "-0.02em", mt: 0, mb: 2, color: "text.primary" },
  "& h2": { fontSize: { xs: "1.05rem", md: "1.15rem" }, fontWeight: 700, mt: 4, mb: 1.5, color: "primary.main" },
  "& h3": { fontSize: "0.95rem", fontWeight: 700, mt: 3, mb: 1, color: "primary.main" },
  "& p": { fontSize: "0.9rem", lineHeight: 1.75, color: "text.secondary", my: 1.5 },
  "& ul": { pl: 2.5, my: 1.5 },
  "& li": { fontSize: "0.9rem", lineHeight: 1.75, color: "text.secondary", mb: 0.75 },
  "& strong": { color: "text.primary", fontWeight: 600 }
};

function LegalView() {
  const { page } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // These are Apointa's own terms — Apointa (FZC) is the contracting party and
  // the payment processing described here is Apointa's. So the page wears the
  // Apointa palette (the createWidgetTheme defaults, same teal as partners),
  // never the club's colour, even when it's reached from inside a branded club.
  const theme = useMemo(() => createWidgetTheme(), []);

  const active = PAGES.find((p) => p.path === page);

  // Opened in a new tab from checkout there is nothing to go back to, so the
  // back link only appears when this page was reached from within the app.
  const canGoBack = location.key && location.key !== "default";

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 3, md: 6 } }}>
        <Container maxWidth="md">
          {canGoBack && (
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon sx={{ fontSize: "1.125rem" }} />}
              sx={{
                mb: 1.5,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.75rem",
                fontWeight: 700,
                px: 0,
                "&:hover": { background: "transparent", color: "primary.main" }
              }}
            >
              Back
            </Button>
          )}

          <Typography
            component="h1"
            sx={{ fontSize: { xs: "1.375rem", md: "1.75rem" }, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", mb: { xs: 2, md: 3 } }}
          >
            {active?.label || "Legal"}
          </Typography>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: (theme) => alpha(theme.palette.divider, 0.6),
              overflow: "hidden",
              bgcolor: "background.paper"
            }}
          >
            <Box sx={{ display: "flex", gap: 1, p: { xs: 1.5, md: 2 }, flexWrap: "wrap" }}>
              {PAGES.map((p) => {
                const isActive = p.path === page;
                return (
                  <Button
                    key={p.path}
                    component={RouterLink}
                    to={`/legal/${p.path}`}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 1.5,
                      color: isActive ? "primary.main" : "text.secondary",
                      bgcolor: (theme) => (isActive ? alpha(theme.palette.primary.main, 0.1) : "transparent"),
                      "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, isActive ? 0.14 : 0.06) }
                    }}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 2.5, md: 4 }, ...COPY_SX }}>
              {page === "terms" ? (
                <TermsContent />
              ) : page === "privacy" ? (
                <PrivacyContent />
              ) : (
                <Typography color="text.secondary">Page not found.</Typography>
              )}
            </Box>
          </Paper>

          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", mt: 3, color: "text.disabled", textTransform: "uppercase", letterSpacing: 1 }}
          >
            Powered by Apointa
          </Typography>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default LegalView;
